import { formatBytes } from 'shared/utils/format-bytes';
import { CompressImageError, CompressImageResult, TARGET_MAX_BYTES } from 'shared/utils/image/compress-image';

/**
 * Тексты о том, что произошло с приложенным изображением.
 *
 * Живут рядом с самим сжатием, а не в форме, потому что говорит их дропзона —
 * общий компонент кита `ud-form`, а кит не имеет права импортировать код фичи.
 * Обратная связь тут единственная: изображение подменяется молча, и без
 * сообщения человек не узнает ни что файл сжали, ни почему его не приняли.
 *
 * Знания про `react-dropzone` в этом файле нет намеренно: `describeRejection`
 * принимает коды ошибок строками, а не `FileRejection`, чтобы `shared` не
 * зависел от библиотеки, выбранной в UI-ките.
 */

/** Предел на исходный файл: сверх этого не читаем в память даже ради сжатия. */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/**
 * Форматы Apple. Браузер их не декодирует, а сконвертировать на клиенте нечем,
 * поэтому единственное, что можно сделать, — объяснить, где переключить формат.
 */
const HEIC_MESSAGE =
  'Формат HEIC не поддерживается. Включите «Наиболее совместимый» в Настройки → Камера → Форматы, или выберите изображение в JPEG';

const UNREADABLE_MESSAGE = 'Не удалось прочитать файл. Подойдут JPG, PNG или WebP';
const PROCESSING_FAILED_MESSAGE = 'Не удалось обработать изображение. Попробуйте другое';

const formatSize = (size: { width: number; height: number }) => `${size.width}×${size.height}`;

/** Достаточно для распознавания формата: имя даёт расширение, тип — MIME. */
export type FileDescription = Pick<File, 'name' | 'type'>;

/** HEIC часто приезжает с пустым `type`, поэтому проверяется и расширение. */
export function isHeicFile(file: FileDescription): boolean {
  return /^image\/heic|^image\/heif/.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

/**
 * Почему файл не приняла сама дропзона (до какой-либо обработки).
 * `codes` — коды `react-dropzone` (`file-too-large`, `file-invalid-type`, …).
 */
export function describeRejection(file: FileDescription, codes: string[]): string {
  if (isHeicFile(file)) return HEIC_MESSAGE;

  if (codes.includes('file-too-large')) {
    return `Файл слишком большой. Выберите изображение до ${formatBytes(MAX_SOURCE_BYTES)}`;
  }

  return UNREADABLE_MESSAGE;
}

/**
 * Сколько картинок разрешено набрать.
 *
 * Слово «фото» выбрано не для краткости: оно не склоняется по числу, и одно
 * сообщение годится и для «не больше 1», и для «не больше 5» — без ветвлений и
 * без утилиты склонения, которой в проекте нет.
 */
export function describeFileCountLimit(maxFiles: number): string {
  if (maxFiles === 1) return 'Можно добавить только одно фото';

  return `Можно добавить не больше ${maxFiles} фото`;
}

/**
 * Что стало с изображением после обработки. `undefined` — значит файл не трогали
 * и сообщать нечего.
 */
export function describeCompression(result: CompressImageResult): string | undefined {
  if (!result.wasCompressed || !result.originalSize || !result.resultSize) return undefined;

  const weights = `${formatBytes(result.originalBytes)} → ${formatBytes(result.resultBytes)}`;

  if (result.keptResolution) {
    return `Изображение сжато: ${weights}. Разрешение ${formatSize(result.originalSize)} сохранено`;
  }

  return `Изображение уменьшено: ${weights}, ${formatSize(result.originalSize)} → ${formatSize(result.resultSize)}`;
}

/** Почему обработка не удалась. `file` нужен, чтобы отличить HEIC от битого файла. */
export function describeImageFailure(error: unknown, file: FileDescription): string {
  if (!(error instanceof CompressImageError)) return PROCESSING_FAILED_MESSAGE;

  if (error.reason === 'decode-failed') {
    return isHeicFile(file) ? HEIC_MESSAGE : UNREADABLE_MESSAGE;
  }

  if (error.reason === 'too-large') {
    return `Не удалось сжать изображение до ${formatBytes(TARGET_MAX_BYTES)}. Выберите файл меньшего размера`;
  }

  return PROCESSING_FAILED_MESSAGE;
}
